import React, { createRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DirectionsRenderer, GoogleMap, InfoWindow, Marker, useGoogleMap, useJsApiLoader, useLoadScript } from '@react-google-maps/api';
import axios from 'axios';
import IGenericResult from '../../interfaces/IGenericResult';
import googleMapReact, { fitBounds } from 'google-map-react';
import { getCurrentPosition } from '../../lib/util';
import { AppContext } from '../../context/AppContext';
import { IAppContext } from '../../context/DefaultAppState';

export interface IMarker extends IGenericResult {
  lat: number;
  lng: number;
  label: string;
}

export interface ILatLng {
  lat: number;
  lng: number;
}

const libraries: any = ['places', 'geometry'];

const containerStyle = {
  width: '100%',
  height: '100%'
};

// Coords of 
const center = {
  lat: 41.850033,
  lng: -87.6500523
};

const stateData = [
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  },
  {
    abbrev: '',
    coords: {
      lat: '',
      lng: ''
    }
  }
];

/*
  https://www.npmjs.com/package/@react-google-maps/api
*/

const Map = (props: any) => {
  const appContext = useContext(AppContext);
  const { isLoaded, loadError } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
    libraries
  });
  const [googleMap, setGoogleMap] = useState<GoogleMap | null>(null);
  const [markers, setMarkers] = useState<IMarker[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<IMarker | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number }>();
  const [directions, setDirections] = useState<google.maps.DirectionsResult | undefined>(undefined);

  useEffect(() => {
    getLatLng(props.currentChunk, setMarkers, setCurrentLocation, props.planState, appContext);
    if (props.profileDirections) {
      setDirections(props.profileDirections);
    }
    // fitMarkerBounds(googleMap);
  }, [props.currentChunk, props.profileDirections]);

  useEffect(() => {
    fitMarkerBounds(googleMap);
  }, [markers]);

  const fitMarkerBounds = (map: any) => {
    if (window.google && !props.profileDirections) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => {
        bounds.extend({ lat: m.lat, lng: m.lng });
      });
      map?.fitBounds(bounds, 60);
    }
  };

  const onLoad = useCallback((map) => {
    setGoogleMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    setGoogleMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={10} onLoad={onLoad} onUnmount={onUnmount}>
      {/* Child components, such as markers, info windows, etc. */}
      {markers?.map((marker, index) => (
        <Marker label={marker.label} key={index} position={{ lat: marker.lat, lng: marker.lng }} onClick={() => setSelectedMarker(marker)} />
      ))}
      <DirectionsRenderer directions={directions} />
      {selectedMarker ? (
        <InfoWindow position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }} onCloseClick={() => setSelectedMarker(null)}>
          <div>
            <h3>{selectedMarker.name}</h3>
            <p>{selectedMarker.phone}</p>
            <p>
              {selectedMarker.addressLine1}, {selectedMarker.addressLine2 ? selectedMarker.addressLine2 + ', ': ''}{selectedMarker.city}, {selectedMarker.state} {selectedMarker.zipCode}
            </p>
            {/* <button onClick={() => props.history.push('/profile')}>View Profile</button>*/}
            <button onClick={() => getDirections(currentLocation, { lat: selectedMarker.lat, lng: selectedMarker.lng }, setDirections)}>Get Directions</button>
          </div>
        </InfoWindow>
      ) : null}
    </GoogleMap>
  ) : (
    <></>
  );
};

// Delete all?
const getLatLng = (
  chunk: IGenericResult[],
  setMarkers: React.Dispatch<React.SetStateAction<IMarker[]>>,
  setCurrentLocation: React.Dispatch<
    React.SetStateAction<
      | {
          lat: number;
          lng: number;
        }
      | undefined
    >
  >,
  plansState: string,
  appContext: IAppContext
) => {
  (async () => {
    //let firstPass = true;
    try {
      const markers: IMarker[] = [];
      let currentLocation = { lat: 0, lng: 0 };
      //const res: any = await getCurrentPosition();
      //const res: any = appContext.origin;
      //const { latitude, longitude } = res;
      //currentLocation.lat = latitude;
      //currentLocation.lng = longitude;
      //setCurrentLocation(currentLocation);

      for (let i = 0; i < chunk.length; i++) {
        const currentChunk = chunk[i];
        if(currentChunk) {
          const formattedAddress = `${currentChunk.addressLine1} ${currentChunk.addressLine2 ? currentChunk.addressLine2 + ' ' : ''}${currentChunk.city} ${currentChunk.state} ${currentChunk.zipCode}`.split(' ').join('+');
          const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${formattedAddress}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
          const locationToMark: { lat: number; lng: number } = geocode.data.results[0]?.geometry?.location;
          if (locationToMark && locationToMark.lat && locationToMark.lng) {
            markers.push({
              recordId: currentChunk.recordId,
              lat: locationToMark.lat,
              lng: locationToMark.lng,
              label: `${i + 1}`,
              name: currentChunk.name,
              locationName: currentChunk.locationName,
              specialty: currentChunk.specialty,
              specialtyGroup: currentChunk.specialtyGroup,
              phone: currentChunk.phone,
              fax: currentChunk.fax,
              addressLine1: currentChunk.addressLine1,
              addressLine2: currentChunk.addressLine2,
              zipCode: currentChunk.zipCode,
              city: currentChunk.city,
              state: currentChunk.state,
              providerNPI: currentChunk.providerNPI
            });
          }
        }
      }
      setMarkers(markers);
      //firstPass = false;
    } catch (err) {
      console.error(err);
    }
    /* if (firstPass) {
      try {
        const markers: IMarker[] = [];
        let currentLocation = { lat: 0, lng: 0 };
        //let planLocation: any;
        const address = `${plansState}`.split(' ').join('+');
        const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
        //planLocation = geocode.data.results[0]?.geometry?.location;
  
        const { latitude, longitude } = geocode.data.results[0]?.geometry?.location;;
        currentLocation.lat = latitude;
        currentLocation.lng = longitude;
        console.log('Lat: ', latitude);
        console.log('Lng: ', longitude);
        setCurrentLocation(currentLocation);
  
        for (let i = 0; i < chunk.length; i++) {
          const currentChunk = chunk[i];
          if(currentChunk) {
            const formattedAddress = `${currentChunk.addressLine1} ${currentChunk.addressLine2 ? currentChunk.addressLine2 + ' ' : ''}${currentChunk.city} ${currentChunk.state} ${currentChunk.zipCode}`.split(' ').join('+');
            const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${formattedAddress}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
            const locationToMark: { lat: number; lng: number } = geocode.data.results[0]?.geometry?.location;
            if (locationToMark && locationToMark.lat && locationToMark.lng) {
              markers.push({
                recordId: currentChunk.recordId,
                lat: locationToMark.lat,
                lng: locationToMark.lng,
                label: `${i + 1}`,
                name: currentChunk.name,
                locationName: currentChunk.locationName,
                specialty: currentChunk.specialty,
                specialtyGroup: currentChunk.specialtyGroup,
                phone: currentChunk.phone,
                fax: currentChunk.fax,
                addressLine1: currentChunk.addressLine1,
                addressLine2: currentChunk.addressLine2,
                zipCode: currentChunk.zipCode,
                city: currentChunk.city,
                state: currentChunk.state,
                providerNPI: currentChunk.providerNPI
              });
            }
          }
        }
        setMarkers(markers);
      } catch (err) {
        console.error(err);
      }
    } */
  })();
};

const getDirections = (origin: any, destination: any, setDirections: any) => {
  const DirectionsService = new google.maps.DirectionsService();

  DirectionsService.route(
    {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      travelMode: google.maps.TravelMode.DRIVING
    },
    (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error(`error fetching directions ${result}`);
      }
    }
  );
};

export default Map;
